import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod/v4';

import { DateInput } from '@shieldpay/bumblebee/date-input';
import { Form } from '@shieldpay/bumblebee/form';
import { Heading } from '@shieldpay/bumblebee/heading';
import DateInputMarkdown from '@shieldpay/bumblebee/markdown/date-input.md';
import { Text } from '@shieldpay/bumblebee/text';

import { dayIsValid } from '@shieldpay/utility-functions-ui/validators/date';

import { ContentsTable } from '~/components/contents-table/contents-table';
import { MdxFormatter } from '~/components/mdx-formatter/mdx-formatter';
import { ComponentPreview } from '~/components/preview/component-preview';
import { createMeta } from '~/utilities/create-meta';

export const meta = createMeta({
  id: 'hive.components.dateInput.title',
});

const FieldSchema = z.object({
  date: z
    .object({
      day: z
        .string({ error: 'Day is required' })
        .refine(
          (val) => z.coerce.number().min(1).max(31).safeParse(val).success,
          { message: 'Day must be between 01 and 31' },
        ),
      month: z
        .string({ error: 'Month is required' })
        .refine(
          (val) => z.coerce.number().min(1).max(12).safeParse(val).success,
          { message: 'Month must be between 01 and 12' },
        ),
      year: z.string({ error: 'Year is required' }),
    })
    .refine((data) => {
      if (!dayIsValid(data)) return false;

      return z.coerce
        .date()
        .min(new Date())
        .safeParse([data.year, data.month, data.day].join('-')).success;
    }),
  readonly: z.object({
    day: z.string(),
    month: z.string(),
    year: z.string(),
  }),
});

export const DateInputComponentPage = () => {
  const [form, fields] = useForm({
    defaultNoValidate: false,
    defaultValue: {
      readonly: {
        day: '24',
        month: '02',
        year: '2030',
      },
    },
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate: ({ formData }) =>
      parseWithZod(formData, {
        schema: FieldSchema,
      }),
  });

  const fieldset = fields.date.getFieldset();
  const readonlyFieldset = fields.readonly.getFieldset();

  return (
    <>
      <Heading>
        <FormattedMessage id="hive.components.dateInput.title" />
      </Heading>

      <ContentsTable />

      <ComponentPreview>
        <Text paragraph>Date input</Text>
        <Form {...getFormProps(form)} spacing="basePos8">
          <DateInput
            name={fields.date.name}
            id={fields.date.id}
            label="Date (interactive) will not show validation until values entered"
            dayInputProps={{
              ...getInputProps(fieldset.day, { type: 'text' }),
              errors: fieldset.day.errors,
              label: 'Day',
              helpText: 'e.g. 24',
              placeholder: 'DD',
            }}
            monthInputProps={{
              ...getInputProps(fieldset.month, { type: 'text' }),
              errors: fieldset.month.errors,
              label: 'Month',
              helpText: 'e.g. 02',
              placeholder: 'MM',
            }}
            yearInputProps={{
              ...getInputProps(fieldset.year, { type: 'text' }),
              errors: fieldset.year.errors,
              label: 'Year',
              helpText: 'e.g. 2030',
              placeholder: 'YYYY',
            }}
          />
          <DateInput
            readOnly
            id={fields.date.id}
            name={fields.date.name}
            label="Date (readonly)"
            dayInputProps={{
              ...getInputProps(readonlyFieldset.day, { type: 'text' }),
              label: 'Day',
              helpText: 'e.g. 24',
              placeholder: 'DD',
            }}
            monthInputProps={{
              ...getInputProps(readonlyFieldset.month, { type: 'text' }),
              label: 'Month',
              helpText: 'e.g. 02',
              placeholder: 'MM',
            }}
            yearInputProps={{
              ...getInputProps(readonlyFieldset.year, { type: 'text' }),
              label: 'Year',
              helpText: 'e.g. 2030',
              placeholder: 'YYYY',
            }}
          />
        </Form>
      </ComponentPreview>

      <MdxFormatter md={DateInputMarkdown} />
    </>
  );
};

export default DateInputComponentPage;
